import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Menu, ChevronRight } from 'lucide-react';
import { ProjectSidebar } from '@/components/ProjectSidebar';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useIsMobile } from '@/hooks/useIsMobile';
import { type Project } from '@/lib/ProjectsManager';
import { getSettingsItems, getSettingsCategories } from '@/lib/settingsItems';
import { cn } from '@/lib/utils';

const settingsItems = getSettingsItems();
const categories = getSettingsCategories();

export function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const projectsManager = useProjectsManager();

  const [_projects, setProjects] = useState<Project[]>([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(!isMobile);
  const [hasSetInitialState, setHasSetInitialState] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      await projectsManager.init();
      const projectList = await projectsManager.getProjects();
      setProjects(projectList);

      // Set initial sidebar state based on projects and device type
      if (!hasSetInitialState && !isMobile) {
        // On desktop, show sidebar if there are projects
        setIsSidebarVisible(true);
        setHasSetInitialState(true);
      } else if (!hasSetInitialState && isMobile) {
        // On mobile, always start hidden
        setIsSidebarVisible(false);
        setHasSetInitialState(true);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      if (!hasSetInitialState) {
        setHasSetInitialState(true);
      }
    }
  }, [projectsManager, hasSetInitialState, isMobile]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectSelect = (project: Project | null) => {
    if (project) {
      setIsSidebarVisible(false); // Collapse sidebar on mobile navigation
      navigate(`/project/${project.id}`);
    } else {
      navigate('/');
    }
  };

  // Get current settings page
  const currentPath = location.pathname;
  const currentSettingsId = settingsItems.find(item => item.href === currentPath)?.id;

  // Group items by category
  const groupedItems = {
    general: settingsItems.filter(item => item.category === 'general'),
    integrations: settingsItems.filter(item => item.category === 'integrations'),
    advanced: settingsItems.filter(item => item.category === 'advanced'),
  };

  if (isMobile) {
    return (
      <div className="min-h-dvh pb-safe bg-background">
        {/* Mobile Header */}
        <header className="pt-safe bg-background sticky top-0 z-40">
          <div className="flex items-center space-x-3 h-14 border-b px-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarVisible(!isSidebarVisible)}
              aria-label="Toggle sidebar"
              className="h-9 w-9 p-0"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">Settings</h1>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {isSidebarVisible && (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
            <div
              className="absolute inset-0"
              onClick={() => {
                // Don't close if dropdown is open
                if (!isDropdownOpen) {
                  setIsSidebarVisible(false);
                }
              }}
            />
            <div className="relative left-0 top-0 h-full w-80 max-w-[80vw] bg-background border-r shadow-lg z-10">
              <ProjectSidebar
                selectedProject={null}
                onSelectProject={(project) => {
                  setIsSidebarVisible(false);
                  handleProjectSelect(project);
                }}
                onClose={() => setIsSidebarVisible(false)}
                onDropdownOpenChange={setIsDropdownOpen}
              />
            </div>
          </div>
        )}

        <Outlet />
      </div>
    );
  }

  // Desktop 3-column layout
  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Left Column - Projects Sidebar (optionally hidden) */}
      {isSidebarVisible && (
        <div className="w-72 border-r bg-sidebar flex-shrink-0">
          <ProjectSidebar
            selectedProject={null}
            onSelectProject={handleProjectSelect}
            className="h-full"
            onToggleSidebar={() => setIsSidebarVisible(false)}
            onDropdownOpenChange={setIsDropdownOpen}
          />
        </div>
      )}

      {/* Middle Column - Settings Navigation */}
      <div className="w-72 border-r bg-muted/30 flex flex-col flex-shrink-0">
        {/* Header with toggle button */}
        <div className="h-14 px-4 border-b flex items-center flex-shrink-0 bg-background/50">
          <div className="flex items-center gap-3">
            {!isSidebarVisible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarVisible(true)}
                className="h-9 w-9 p-0"
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-lg font-semibold tracking-tight">
              Settings
            </h1>
          </div>
        </div>

        {/* Settings Navigation - Scrollable */}
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-6">
            {/* General */}
            <div>
              <h2 className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {categories.general}
              </h2>
              <div className="space-y-1">
                {groupedItems.general.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentSettingsId === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.href)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 flex items-center gap-3 group',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className={cn(
                        'h-4 w-4 flex-shrink-0 transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      )} />
                      <span className="text-sm font-medium flex-1">{item.title}</span>
                      <ChevronRight className={cn(
                        'h-4 w-4 opacity-0 -translate-x-1 transition-all',
                        isActive && 'opacity-100 translate-x-0',
                        'group-hover:opacity-50 group-hover:translate-x-0'
                      )} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Integrations */}
            <div>
              <h2 className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {categories.integrations}
              </h2>
              <div className="space-y-1">
                {groupedItems.integrations.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentSettingsId === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.href)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 flex items-center gap-3 group',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className={cn(
                        'h-4 w-4 flex-shrink-0 transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      )} />
                      <span className="text-sm font-medium flex-1">{item.title}</span>
                      <ChevronRight className={cn(
                        'h-4 w-4 opacity-0 -translate-x-1 transition-all',
                        isActive && 'opacity-100 translate-x-0',
                        'group-hover:opacity-50 group-hover:translate-x-0'
                      )} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Advanced */}
            <div>
              <h2 className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {categories.advanced}
              </h2>
              <div className="space-y-1">
                {groupedItems.advanced.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentSettingsId === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.href)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 flex items-center gap-3 group',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className={cn(
                        'h-4 w-4 flex-shrink-0 transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      )} />
                      <span className="text-sm font-medium flex-1">{item.title}</span>
                      <ChevronRight className={cn(
                        'h-4 w-4 opacity-0 -translate-x-1 transition-all',
                        isActive && 'opacity-100 translate-x-0',
                        'group-hover:opacity-50 group-hover:translate-x-0'
                      )} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Right Column - Settings Content - Scrollable */}
      <ScrollArea className="flex-1 bg-background">
        <div className="max-w-2xl">
          <Outlet />
        </div>
      </ScrollArea>
    </div>
  );
}
